import * as Sentry from '@sentry/browser';
import GCInstant from '@play-co/gcinstant';

import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';

import {
  ClientReplicant,
  Replicant,
  createClientReplicant,
  parseReplicantPlatform,
  createWebPlayableReplicant,
  ReplicantError,
  hasWebPlayerCredentials,
} from '@play-co/replicant';

import reducers from 'src/state/reducers';
import { State } from 'src/state';
import replicantConfig from 'src/replicant/config';
import { State as ReplicantState, StateFromSchema } from 'src/replicant/State';
import { update } from 'src/state/user';
import { onError } from './lib/errors';
import { sentryForRedux, payloadToData } from 'src/lib/sentry';
import { PerformanceMetricsTracker } from './lib/PerformanceAnalytics';
import { Locale } from './replicant/ruleset/locale';

type Listener = (state: State) => void;

// Dev mode settings
// WARNING: immutableCheck and serializableCheck will be switched to false on production
// See: https://github.com/reduxjs/redux-toolkit/blob/659ff141edf32f68a5b67072d8c161625b369024/src/getDefaultMiddleware.ts#L57
const storeConfig = {
  devTools: true,
  immutableCheck: true,
  serializableCheck: true,
};

// Three seconds is just enough to capture an individual spin -> consume cycle.
// Also, do not batch requests in local dev when FB is simulated.
const BATCHING_MAX_TIME = process.env.SIMULATED ? 0 : 3000;

// TODO A/B test this (maybe...)
// Check for messages this often. We want to feel pretty realtime.
const HEARTBEAT_INTERVAL = 10000; // target: 10s

// TODO A/B test this
// Load friend states this often. We don't want stale friend data.
const STATE_LOAD_INTERVAL = 30000; // target: 10s

// For happy fun dev times!
const simulatedLatencyOffline = 0;

class StateObserver {
  private store = configureStore({
    reducer: reducers,
    devTools: storeConfig.devTools,
    middleware: getDefaultMiddleware({
      immutableCheck: storeConfig.immutableCheck,
      serializableCheck: storeConfig.serializableCheck,
    }).concat(sentryForRedux),
  });

  public replicant?: ClientReplicant<
    Replicant<{
      state: StateFromSchema;
      actions: typeof replicantConfig.actions;
      asyncGetters: typeof replicantConfig.asyncGetters;
      ruleset: typeof replicantConfig.ruleset;
    }>
  >;

  invoke: StateObserver['replicant']['invoke'];

  performanceTracker = new PerformanceMetricsTracker('user/update');
  listenerTracker = new PerformanceMetricsTracker('SO/listeners');
  replicantClientPromise = new Promise<ClientReplicant<any>>((resolve) => {
    this.resolveReplicantClient = resolve;
  });

  private listeners: Array<Listener>;
  private onDetachListenersHandlers: Array<() => void> = [];
  private resolveReplicantClient: (
    replicantClient: ClientReplicant<any>,
  ) => void;

  initLocal() {
    if (this.listeners) {
      throw new Error('StateObserver.init Error: Already initialized.');
    }
    this.listeners = [];
    this.store.subscribe(() =>
      this.listeners.forEach((x) => x(this.store.getState())),
    );
  }

  async init() {
    this.initLocal();

    const platform = parseReplicantPlatform(process.env.PLATFORM);

    if (!platform) {
      throw Error(`Invalid Replicant platform: ${process.env.PLATFORM}`);
    }

    if (platform === 'web') {

      // retry flow for falling back token login to web credentials
      const loginToken = GCInstant.entryData['$loginToken'];
      try {
        this.replicant = await createWebPlayableReplicant(replicantConfig, {
          platform,
          endpoint: process.env.REPLICANT_ENDPOINT,
          loginToken,
          debug: Boolean(process.env.IS_DEVELOPMENT),
        });
      } catch(e) {
        if (loginToken && hasWebPlayerCredentials(replicantConfig.appName)) {
          this.replicant = await createWebPlayableReplicant(replicantConfig, {
            platform,
            endpoint: process.env.REPLICANT_ENDPOINT,
            debug: Boolean(process.env.IS_DEVELOPMENT),
          });
        } else {
          throw e;
        }
      }
      
    } else {
      let storeKey = 'default';

      this.replicant = await createClientReplicant(
        replicantConfig,
        'offline-player',
        {
          // Common opts:
          platform: 'mock',
          endpoint: process.env.REPLICANT_ENDPOINT,
          // batchingMaxTime: BATCHING_MAX_TIME,
          // checkForMessagesInterval: HEARTBEAT_INTERVAL,
          // refreshFriendsStatesInterval: STATE_LOAD_INTERVAL,
          // kvStore: {
          //  prefetchKeys: GCInstant.entryData.$key && [GCInstant.entryData.$key],
          //},

          // Online opts:
          // signature: GCInstant.playerSignature,
          // obtainSignature: () => GCInstant.refreshPlayerSignature(),

          // Offline opts:
          // latency: simulatedLatencyOffline,
        },
      );
    }

    this.replicant.setOnError(onError);

    this.replicant.setActionPreInvokeCallback((action, args) => {
      Sentry.addBreadcrumb({
        category: 'replicant-action',
        message: action,
        level: 'info',
        data: payloadToData(args),
      });
    });

    this.replicant.setActionFailedCallback((action, args) => {
      Sentry.addBreadcrumb({
        category: 'replicant-action-failed',
        message: action,
        level: 'info',
        data: payloadToData(args),
      });
    });

    this.invoke = this.replicant.invoke;

    this.attachStoreToReplicant();

    this.resolveReplicantClient(this.replicant);
  }

  //

  addListener(listener: Listener, opts?: { noInit?: boolean }) {
    if (!this.listeners) {
      throw new Error('StateObserver.addListener Error: Not initialized.');
    }

    if (this.listeners.includes(listener)) {
      throw new Error(
        'this.removeListener Error: Listener already subscribed.',
      );
    }

    this.listeners.push(listener);

    opts?.noInit || listener(this.store.getState());

    return listener;
  }

  removeListener(listener: Listener) {
    if (!this.listeners) {
      throw new Error('StateObserver.removeListener Error: Not initialized.');
    }

    const index = this.listeners.indexOf(listener);

    if (index < 0) {
      throw new Error('this.removeListener Error: Listener not found.');
    }

    this.listeners.splice(index, 1);
  }

  detachListeners() {
    this.onDetachListenersHandlers.forEach((handler) => handler());

    for (const listener of this.listeners) {
      this.removeListener(listener);
    }
  }

  //

  getState() {
    return this.store.getState();
  }

  readonly dispatch: StateObserver['store']['dispatch'] = this.store.dispatch.bind(
    this.store,
  );

  now() {
    return this.replicant.now();
  }

  getSessionData() {
    return this.replicant.getChatbotSessionData();
  }

  onDetachListeners(handler: () => void) {
    this.onDetachListenersHandlers.push(handler);
  }

  resetAppState() {
    this.store.dispatch({
      type: 'RESET_APP',
      payload: this.store.getState().user,
    });
  }

  async refreshUserState(refreshSignature: boolean): Promise<boolean> {
    // Disable redux state updates until `onLogin` is done.
    this.detachStoreFromReplicant();

    try {
      await this.replicant.refresh(
        refreshSignature ? GCInstant.playerSignature : null,
      );

      await this.invoke.onLogin();
      return true;
    } catch (err) {
      // This error will get forwaded to `onError`
      return false;
    } finally {
      // Re-enable updates.
      this.attachStoreToReplicant();
    }
  }

  async refreshMessages() {
    await this.replicant.flush();
    await this.replicant.checkForMessages();
  }

  private attachStoreToReplicant() {
    this.onStateChanged(this.replicant.state);
    this.replicant.onStateChanged(this.onStateChanged);
  }

  private detachStoreFromReplicant() {
    this.replicant.removeStateChangedHandler(this.onStateChanged);
  }

  private onStateChanged = (state: ReplicantState) => {
    // Measure the listeners before dispatching.
    this.listenerTracker.measure(this.listeners.length);

    const start = performance.now();
    this.store.dispatch(update(state));
    const end = performance.now();
    this.performanceTracker.measure(end - start);
  };

  /**
   * Get current user locale
   */
  public getLocale(): Locale {
    return this.getState().user.settings.locale as Locale;
  }
}

const instance = new StateObserver();

export default instance;

// Expose singleton StateObserver to window in development mode
if (process.env.IS_DEVELOPMENT) {
  (window as any).StateObserver = instance;
  (window as any).GCInstant = GCInstant;
}
